import { useEffect } from "react";
import { SITE_BASE } from "../../lib/siteRouting";
import { CONTACT_PHONE } from "../../config/contact";

const DEFAULTS = {
  title: "TPCO Sénégal — Touba Pro Construction · Construire autrement et mieux",
  description: "TPCO Sénégal — Entreprise de construction, promotion immobilière et gestion locative à Dakar. Résidences haut standing, lots de villas et catalogue béton (planelle, hourdis, bloc, brique).",
  image: "/logo-tpco.png",
  type: "website",
  siteName: "TPCO Sénégal",
};

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, k, v] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
    if (k && v) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value || "");
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.head.querySelector(`script[id="${id}"]`);
  if (el) el.remove();
  if (!data) return;
  el = document.createElement("script");
  el.setAttribute("type", "application/ld+json");
  el.setAttribute("id", id);
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

/**
 * Set SEO meta tags on a public page. React-friendly (no extra lib).
 * @param {Object} props
 *  - title (required) page title
 *  - description optional meta description
 *  - image absolute or relative URL for og:image
 *  - canonical optional canonical URL
 *  - jsonLd optional structured data object
 */
export default function SEOHead({ title, description, image, canonical, jsonLd, type = "website" }) {
  useEffect(() => {
    const fullTitle = title ? `${title} | TPCO Sénégal` : DEFAULTS.title;
    const desc = description || DEFAULTS.description;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = canonical || (typeof window !== "undefined" ? window.location.href : "");
    const img = image && image.startsWith("http") ? image : `${origin}${image || DEFAULTS.image}`;

    document.title = fullTitle;
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[name="robots"]', "content", "index, follow, max-image-preview:large");
    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:image"]', "content", img);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:site_name"]', "content", DEFAULTS.siteName);
    setMeta('meta[property="og:locale"]', "content", "fr_SN");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", img);
    setMeta('meta[name="geo.region"]', "content", "SN-DK");
    setMeta('meta[name="geo.placename"]', "content", "Dakar, Sénégal");
    setLink("canonical", url);

    setJsonLd("page-jsonld", jsonLd);

    return () => {
      // keep meta on unmount to avoid flash; next page will overwrite
    };
  }, [title, description, image, canonical, jsonLd, type]);

  return null;
}

/** Static Organization JSON-LD — included on every page via SiteLayout root. */
export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  "name": "TPCO Sénégal",
  "alternateName": "Touba Pro Construction",
  "url": typeof window !== "undefined" ? window.location.origin + (SITE_BASE || "/") : "",
  "logo": typeof window !== "undefined" ? window.location.origin + "/logo-tpco.png" : "/logo-tpco.png",
  "description": DEFAULTS.description,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Hann Maristes 1, lot F/06/A",
    "addressLocality": "Dakar",
    "addressCountry": "SN",
  },
  "telephone": CONTACT_PHONE.replace(/\s/g, ""),
  "email": "contact@tpco.sn",
  "areaServed": "Dakar, Sénégal",
  "sameAs": ["https://tpco.sn"],
};
